/* eslint-disable no-param-reassign */
import { unwrapElement } from ".";
import type { FroalaEditorInstance } from "../components/froala/types";

interface ListFormatOptions {
  icon?: string;
  unicode?: string;
}

const MAPPED_STYLES: Record<string, { name: string; key: string }> = {
  fontSize: {
    name: "font-size",
    key: "--[default]-size",
  },
  fontFamily: {
    name: "font-family",
    key: "--[default]-font",
  },
  fontWeight: {
    name: "font-weight",
    key: "--[default]-weight",
  },
  fontStyle: {
    name: "font-style",
    key: "--[default]-style",
  },
  lineHeight: {
    name: "line-height",
    key: "--[default]-line-height",
  },
  color: {
    name: "color",
    key: "--[default]-color",
  },
};

export const handleListFormat = (
  editor: FroalaEditorInstance,
  value: string,
  options: ListFormatOptions = {},
) => {
  const el: HTMLElement | null = editor.selection.element();
  if (!el) return;
  if (value === "none") {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) {
      // Browser selection lost (e.g. toolbar click moved focus) — use Froala's tracked element
      const li = el.closest("li");
      const list = li?.closest("ul") || li?.closest("ol");
      if (!li || !list) return;
      unwrapListElement(list as HTMLElement);
      return;
    }

    // Determine the actual LI(s) from the selection range boundaries
    // instead of selection.blocks() which can include adjacent items
    const range = sel.getRangeAt(0);
    const startNode =
      range.startContainer instanceof HTMLElement
        ? range.startContainer
        : range.startContainer.parentElement;
    const endNode =
      range.endContainer instanceof HTMLElement
        ? range.endContainer
        : range.endContainer.parentElement;

    const startLi = startNode?.closest("li") as HTMLElement | null;
    const endLi = endNode?.closest("li") as HTMLElement | null;

    if (!startLi) {
      // Fallback: use selection.element()
      const li = el.closest("li");
      const list = li?.closest("ul") || li?.closest("ol");
      if (!li || !list) return;
      unwrapListElement(list as HTMLElement);
      return;
    }

    // Collapsed cursor (no text selected): remove the entire list
    if (sel.isCollapsed) {
      const list = startLi.closest("ul") || startLi.closest("ol");
      if (!list) return;
      unwrapListElement(list as HTMLElement);
      return;
    }

    // Text selection within one item, or full-item selection where browser
    // puts endContainer at offset 0 of the next element
    const endAtBoundary = endLi && endLi !== startLi && range.endOffset === 0;
    if (!endLi || startLi === endLi || endAtBoundary) {
      const list = startLi.closest("ul") || startLi.closest("ol");
      if (!list) return;
      removeSingleListItem(startLi, list as HTMLElement);
      return;
    }

    // Multi-LI selection: collect all LIs between start and end
    const selectedLis = new Set<HTMLElement>();
    const affectedLists = new Set<HTMLElement>();

    // Walk from startLi to endLi collecting all LIs in between
    let current: Element | null = startLi;
    while (current) {
      if (current.tagName === "LI" && current instanceof HTMLElement) {
        selectedLis.add(current);
        const list = current.closest("ul") || current.closest("ol");
        if (list instanceof HTMLElement) affectedLists.add(list);
      }
      if (current === endLi) break;
      // Move to next LI sibling; if none, traverse to the next list's first <li>
      let next: Element | null = current.nextElementSibling;
      if (!next && current.tagName === "LI") {
        const parentList: Element | null =
          current.closest("ul") || current.closest("ol");
        const nextList: Element | null | undefined =
          parentList?.nextElementSibling;
        if (
          nextList &&
          (nextList.tagName === "UL" || nextList.tagName === "OL")
        ) {
          next = nextList.querySelector(":scope > li");
        }
      }
      current = next;
    }

    // Fallback if walk didn't find anything
    if (selectedLis.size === 0) {
      const list = startLi.closest("ul") || startLi.closest("ol");
      if (!list) return;
      removeSingleListItem(startLi, list as HTMLElement);
      return;
    }

    // Process each affected list
    for (const list of Array.from(affectedLists)) {
      const allItems = Array.from(list.querySelectorAll(":scope > li"));
      const selectedInThisList = allItems.filter((item) =>
        selectedLis.has(item as HTMLElement),
      );

      if (selectedInThisList.length === allItems.length) {
        unwrapListElement(list);
      } else {
        for (let i = selectedInThisList.length - 1; i >= 0; i--) {
          removeSingleListItem(selectedInThisList[i] as HTMLElement, list);
        }
      }
    }
    return;
  }

  if (value === "number") editor.lists.format("OL");
  if (["bullet"].includes(value) && !el.closest("ul")) {
    editor.lists.format("UL");
  }
  if (["icons", "checkmark"].includes(value) && !el.closest("ul")) {
    editor.selection.save();
    editor.lists.format("UL");
    const html = editor.html.get();
    editor.html.set("");
    editor.html.set(html);
    editor.selection.restore();
  }

  const selected: HTMLElement | null = editor.selection.blocks()[0];
  if (!selected) return;
  selected.closest("ul")?.removeAttribute("class");
  selected.closest("ol")?.removeAttribute("class");
  setListColor(selected, editor);
  if (value === "checkmark")
    selected.closest("ul")?.classList.add("custom-check-mark");
  if (value === "icons") {
    const list = selected.closest("ul");
    if (!list) return;

    list.setAttribute("data-icon-name", options.icon || "");
    list.classList.add("custom-icon-mark", options.icon || "");

    const li = list.querySelectorAll("li");
    li.forEach((item: HTMLElement) => {
      item.setAttribute("data-icon-name", options.icon || "");
      item.setAttribute(
        "data-icon",
        String.fromCharCode(Number.parseInt(options.unicode || "0", 16)),
      );
    });
  }
};

export const getListType = (el: HTMLElement) => {
  let type = "none";
  if (el.closest("ol")) {
    type = "number";
  } else if (el.closest("ul")) {
    type = "bullet";
    if (el.closest(".custom-check-mark")) type = "checkmark";
    if (el.closest(".custom-icon-mark")) type = "icons";
  }
  return type;
};

export const getListIcon = (el: HTMLElement) => {
  let icon: string | null = null;
  if (el.closest("ul") && !!el.closest(".custom-icon-mark")) {
    const classes = el
      .closest(".custom-icon-mark")
      ?.getAttribute("class")
      ?.split(" ");
    const classIcon = classes?.find((className: string) =>
      className.startsWith("ri-"),
    );
    if (classIcon) icon = classIcon;
  }
  return icon;
};

/** Properties that should use design system var() references instead of computed values */
const DESIGN_SYSTEM_VAR_PROPERTIES = new Set(["fontFamily"]);

const setListColor = (selected: HTMLElement, editor: FroalaEditorInstance) => {
  const ul = selected.closest("ul");
  const ol = selected.closest("ol");
  const list = ul || ol;
  if (!list) return;
  const li = list.querySelectorAll("li");
  li.forEach((item: HTMLElement) => {
    const data = getDeepestSingleNodeStyle(item);
    item.setAttribute("data-origin-tag", data.origin);

    const variablePrefix =
      data.origin === "h2"
        ? "--title"
        : data.origin === "h3"
          ? "--subtitle"
          : "--paragraph";

    for (const key in data.styles) {
      if (!data.styles[key] || !MAPPED_STYLES[key]) continue;

      if (DESIGN_SYSTEM_VAR_PROPERTIES.has(key)) {
        // Use design system variable reference instead of resolved computed value
        const varName = MAPPED_STYLES[key].key.replace(
          "--[default]",
          variablePrefix,
        );
        item.style.setProperty(MAPPED_STYLES[key].name, `var(${varName})`);
      } else {
        item.style.setProperty(MAPPED_STYLES[key].name, data.styles[key]);
      }
    }
    if (["h2", "h3"].includes(data.origin)) {
      item.querySelectorAll("h2, h3").forEach((h) => {
        editor.$(h).replaceWith(editor.$(h).html() as string);
      });
    }
  });
};

const getDeepestSingleNodeStyle = (
  el: HTMLElement,
): { styles: Record<string, string>; origin: string } => {
  let current = el;
  while (
    current.childNodes.length === 1 &&
    current.childNodes[0].nodeType === Node.ELEMENT_NODE
  ) {
    current = current.childNodes[0] as HTMLElement;
  }

  const origin = current.closest("h2")
    ? "h2"
    : current.closest("h3")
      ? "h3"
      : current.closest("[data-origin-tag]")?.getAttribute("data-origin-tag") ||
        "p";

  const computedStyles = getComputedStyle(current);
  const styles = Object.keys(MAPPED_STYLES).reduce(
    (acc, style) => {
      const value = computedStyles.getPropertyValue(MAPPED_STYLES[style].name);
      if (value) acc[style] = value;
      return acc;
    },
    {} as Record<string, string>,
  );
  return { styles, origin };
};

const convertLiToBlock = (item: HTMLElement): HTMLElement => {
  const dataOriginTag = item.getAttribute("data-origin-tag") || "p";
  const computedStyles = getComputedStyle(item);
  const styles = Object.keys(MAPPED_STYLES).reduce(
    (acc, style) => {
      const value = computedStyles.getPropertyValue(MAPPED_STYLES[style].name);
      if (value) acc[style] = value;
      return acc;
    },
    {} as Record<string, string>,
  );

  // If the LI's only element child is itself a block element (e.g. Google Docs
  // paste produces <li><p>text</p></li>), use its innerHTML to avoid invalid
  // nesting like <p><p>text</p></p> which the browser splits into empty <p> tags
  const onlyChild = item.children.length === 1 ? item.children[0] : null;
  const innerIsBlock = onlyChild && /^(P|H[1-6]|DIV)$/i.test(onlyChild.tagName);

  const node = document.createElement(dataOriginTag);
  node.innerHTML = innerIsBlock
    ? (onlyChild as Element).innerHTML
    : item.innerHTML;

  const variablePrefix =
    dataOriginTag === "h2"
      ? "--title"
      : dataOriginTag === "h3"
        ? "--subtitle"
        : "--paragraph";

  for (const key in styles) {
    const styleValue = styles[key];
    if (!styleValue || !MAPPED_STYLES[key]) continue;
    const variable = `var(${MAPPED_STYLES[key].key.replace("--[default]", variablePrefix)})`;
    const actualStyle = getDesignSystemVar(variable);
    const actualStyleCleaned = actualStyle?.replace(/['"]+/g, "");
    if (actualStyleCleaned === styleValue) continue;
    node.style.setProperty(MAPPED_STYLES[key].name, styleValue);
  }

  // Preserve text-align from the list item's inline style
  const textAlign = item.style.textAlign || computedStyles.textAlign;
  if (textAlign && textAlign !== "start") {
    node.style.textAlign = textAlign;
  }

  return node;
};

const removeSingleListItem = (li: HTMLElement, list: HTMLElement) => {
  const items = Array.from(list.querySelectorAll(":scope > li"));
  const idx = items.indexOf(li);

  if (items.length === 1) {
    return unwrapListElement(list);
  }

  const block = convertLiToBlock(li);
  const parent = list.parentNode;
  if (!parent) return;

  if (idx === 0) {
    parent.insertBefore(block, list);
    li.remove();
  } else if (idx === items.length - 1) {
    if (list.nextSibling) {
      parent.insertBefore(block, list.nextSibling);
    } else {
      parent.appendChild(block);
    }
    li.remove();
  } else {
    // Middle item: split the list
    const afterList = list.cloneNode(false) as HTMLElement;
    for (let i = idx + 1; i < items.length; i++) {
      afterList.appendChild(items[i]);
    }
    if (list.nextSibling) {
      parent.insertBefore(block, list.nextSibling);
      parent.insertBefore(afterList, block.nextSibling);
    } else {
      parent.appendChild(block);
      parent.appendChild(afterList);
    }
    li.remove();
  }
};

const unwrapListElement = (el: HTMLElement | null) => {
  if (!el) return;
  const li = el.querySelectorAll("li");
  li.forEach((item: HTMLElement) => {
    const node = convertLiToBlock(item);
    item.innerHTML = "";
    item.appendChild(node);
    unwrapElement(item);
  });

  unwrapElement(el);
};

const getDesignSystemVar = (
  variable: string | undefined,
): string | undefined => {
  if (!variable) return;
  const styles = getComputedStyle(document.body, null);
  if (!variable.includes("var")) return variable;
  const varValue = variable.substring(
    variable.lastIndexOf("(") + 1,
    variable.lastIndexOf(")"),
  );
  return styles.getPropertyValue(varValue).trim();
};
